import { configService } from '@/services'
import { getActiveProposal } from '@/lib/assistantAudit'
import { removeEndingSlash, removeStartingSlash } from '@/utils'
import { noteBusinessInactive } from '@/lib/businessStatus'
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'

export type ApiVersion = 'v1' | 'v2'

type RequestConfig = AxiosRequestConfig & {
  url: string
  useBearerToken?: boolean
  bearerToken?: string
  multipart?: boolean
  responseType?: string
  version?: ApiVersion
  onUploadProgress?: (progressEvent: any) => void
}

async function getInstance(options: RequestConfig): Promise<AxiosInstance> {
  const apiConfig = configService.getConfig()?.apiConfig
  const baseUrl = apiConfig?.baseUrl
  const bearerToken = apiConfig?.bearerToken

  const version = options?.version ?? ''
  const hasMultipart = options?.multipart
  const responseType = options?.responseType
  const instance = axios.create({
    baseURL: `${removeEndingSlash(baseUrl)}/${version}/`,
    timeout: options?.timeout ?? Infinity,
    headers: {
      'Content-Type': 'application/json',
    },
    responseType,
  })

  instance.interceptors.request.use(
    async (config: any) => {
      try {
        if (config?.headers) {
          if (typeof options?.useBearerToken === 'undefined' || options?.useBearerToken) {
            let token = localStorage.getItem('access_token')
            if (!token) {
              token = bearerToken
            }

            if (token) {
              config.headers.Authorization = `Bearer ${token}`
            }
          }

          if (options?.bearerToken) {
            config.headers.Authorization = `Bearer ${options?.bearerToken}`
          }

          if (apiConfig?.clientHost) {
            config.headers['X-Client-Host'] = apiConfig.clientHost
          }
          if (apiConfig?.systemUid) {
            config.headers['X-System-Uid'] = apiConfig.systemUid
          }
          const proposalUid = getActiveProposal()
          if (proposalUid && config.method && config.method.toLowerCase() !== 'get') {
            config.headers['X-Copilot-Proposal'] = proposalUid
          }

          if (hasMultipart) {
            config.headers['Content-Type'] = 'multipart/form-data'
          }
          if (responseType === 'blob') {
            config.responseType = 'blob'
            config.maxBodyLength = Infinity
          }

          config.headers = {
            ...config.headers,
            ...options?.headers,
          }
        }
      } catch (e) {}

      return config
    },
    (error) => Promise.reject(error as Error)
  )
  instance.interceptors.response.use(undefined, (error) => {
    noteBusinessInactive(error)
    return Promise.reject(error as Error)
  })

  return instance
}

const httpClient = {
  async get(options: RequestConfig) {
    const instance: AxiosInstance = await getInstance(options)

    return instance
      .get(removeStartingSlash(options.url))
      .then((res) => res)
      .catch((reason) => Promise.reject(reason as Error))
  },

  async post(options: RequestConfig) {
    const instance: AxiosInstance = await getInstance(options)

    return instance
      .post(removeStartingSlash(options.url), options.data, {
        onUploadProgress: options.onUploadProgress,
      })
      .then((res) => res)
      .catch((reason) => Promise.reject(reason as Error))
  },

  async put(options: RequestConfig) {
    const instance: AxiosInstance = await getInstance(options)

    return instance
      .put(removeStartingSlash(options.url), options.data, {
        onUploadProgress: options.onUploadProgress,
      })
      .then((res) => res)
      .catch((reason) => Promise.reject(reason as Error))
  },

  async patch(options: RequestConfig) {
    const instance: AxiosInstance = await getInstance(options)

    return instance
      .patch(removeStartingSlash(options.url), options.data, {
        onUploadProgress: options.onUploadProgress,
      })
      .then((res) => res)
      .catch((reason) => Promise.reject(reason as Error))
  },

  async delete(options: RequestConfig) {
    const instance: AxiosInstance = await getInstance(options)

    return instance
      .delete(removeStartingSlash(options.url))
      .then((res) => res)
      .catch((reason) => Promise.reject(reason as Error))
  },
}

export { httpClient }
